import { useContext } from "react";
import * as ReactRouter from "react-router";
import * as sup from "superstruct";
import { ObjectSchema } from "superstruct/lib/utils";

import { notifyBugsnag } from "@/logging";

import {
  composeUrl,
  hasParams,
  hasQuery,
  withParams,
  withQuery,
} from "./helpers/composeUrl";
import {
  createParamsContext,
  createParamsProvider,
} from "./helpers/createParamsProvider";
import {
  createQueryContext,
  createQueryProvider,
} from "./helpers/createQueryProvider";
import {
  BuildUrl,
  CreateRoutingResult,
  InferParams,
  InferQuery,
  RouteType,
  RoutingComponentType,
  RoutingConfig,
  UseRouteParams,
  UseRouteQuery,
} from "./helpers/types";

/**
 * It's a function that creates whole routing for the App.
 * As a parameter it takes the routes configuration:
 *
 * ```
 * {
 *  routeName: defineRoute({
 *   path: ...
 *   queryStruct: ... // optional, needed when route needs "?query=params", remember that you need to manually coerce string value to other primitives (numbers, booleans...) when needed
 *   isExact: ... // optional and defaulted to `true`
 *  })
 * }
 * ```
 *
 * It returns all the modules needed to make routing work with type safety
 * in mind:
 * - `useRouteParams(routeName)` - returns parsed params for given route, basing on params defined in route's path. It returns `null` if route doesn't match
 * - `useRouteQuery(routeName)` - returns parsed query for given route, basing on struct provided in `queryStruct`. It returns `null` if route doesn't match or parsed query cannot pass validation schema.
 * - `buildUrl(routeName [,config])` - returns an URL for the route, if route has params and/or query it also needs `config` param with values for them
 * - `Routes` - a component that connects components with routes and specifies fallthroughComponent (404), under the hood it uses components from `react-router` so it has to be wrapped in `Router` from `react-router`
 * - `config` - unwrapped config provided to `createRouting`
 */
export function createRouting<T extends RoutingConfig>(
  this: void,
  config: T
): CreateRoutingResult<T> {
  const ParamsContext = createParamsContext();
  const ParamsProvider = createParamsProvider(ParamsContext);

  const useRouteParams: UseRouteParams<T> = (routeName) => {
    const paramsContext = useContext(ParamsContext);

    if (routeName === paramsContext.routeName) {
      return paramsContext.params as InferParams<T[keyof T]>;
    }

    const error = new Error(
      `Cannot use params of route "${routeName as string}" inside "${
        paramsContext.routeName
      }"`
    );

    notifyBugsnag(error);
    // eslint-disable-next-line no-console
    console.error(error);

    return null;
  };

  const QueryContext = createQueryContext();
  const QueryProvider = createQueryProvider(QueryContext);

  const useRouteQuery: UseRouteQuery<T> = (routeName) => {
    const queryContext = useContext(QueryContext);

    if (queryContext.routeName === routeName) {
      return queryContext.query as typeof routeName extends keyof T
        ? InferQuery<T[typeof routeName]>
        : never;
    }

    const error = new Error(
      `Cannot use query of route "${routeName as string}" inside "${
        queryContext.routeName
      }"`
    );

    notifyBugsnag(error);
    // eslint-disable-next-line no-console
    console.error(error);

    return null;
  };

  const buildUrl: BuildUrl<T> = (...[routeName, urlConfig]) => {
    const { path } = config[routeName];
    const params = hasParams(urlConfig) ? urlConfig.params : null;
    const query = hasQuery(urlConfig)
      ? (urlConfig.query as Record<string, unknown>)
      : null;
    return composeUrl(path, withParams(params), withQuery(query));
  };

  const Routes: RoutingComponentType<T> = ({
    components,
    fallthroughComponent: FallthroughComponent,
  }) => {
    const routesList = Object.entries<() => JSX.Element>(components);

    return (
      <ReactRouter.Switch>
        {routesList.map(([routeName, Component]) => {
          const { path, isExact = true, queryStruct } = config[
            routeName as keyof T
          ];

          return (
            <ReactRouter.Route key={path} path={path} exact={isExact}>
              <QueryProvider
                routeName={routeName}
                struct={
                  queryStruct instanceof sup.Struct ? queryStruct : undefined
                }
              >
                <ParamsProvider routeName={routeName} path={path}>
                  <Component />
                </ParamsProvider>
              </QueryProvider>
            </ReactRouter.Route>
          );
        })}
        <FallthroughComponent />
      </ReactRouter.Switch>
    );
  };

  return { Routes, useRouteParams, buildUrl, useRouteQuery, config };
}

/**
 * It's a utility that helps to define correctly typed route inside the
 * `createRouting` configuration param.
 */
export function defineRoute<Path extends string, T>(
  routeConfig: string extends Path
    ? // Path must be defined 'as cost' to support :params matching in template
      // strings
      { error: "Path must be defined 'as const'" }
    : RouteType<Path, T>
) {
  return routeConfig;
}

/**
 * It's a utility that helps to define correctly typed nested route inside the
 * `createRouting` configuration param.
 *
 * Example use:
 *
 * ```
 * const route = defineRoute({
 *  path: "/some-route/:param1" as const
 * });
 *
 * const nestedRoute = nestRoute(route, {
 *  path: "/nested/:param2" as const
 * });
 * ```
 *
 * In the above example nested routes path for `route` is
 * `/some-route/:param1/nested/:param2`. Params & query from `route` are
 * automatically passed to `nestedRoute`, and can be extended with those for
 * `nestedRoute`.
 */
export function nestRoute<
  ParentPath extends string,
  ParentQuery,
  Path extends string,
  Query
>(
  parentRouteConfig: RouteType<ParentPath, ParentQuery>,
  nestedRoute: RouteType<Path, Query>
): RouteType<`${ParentPath}${Path}`, ParentQuery & Query> {
  const queryStruct =
    parentRouteConfig.queryStruct && nestedRoute.queryStruct
      ? sup.assign(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          parentRouteConfig.queryStruct as sup.Struct<any, ObjectSchema>,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          nestedRoute.queryStruct as sup.Struct<any, ObjectSchema>
        )
      : parentRouteConfig.queryStruct || nestedRoute.queryStruct;

  return {
    path: `${parentRouteConfig.path}${nestedRoute.path}`,
    queryStruct: queryStruct as sup.Describe<ParentQuery & Query> | undefined,
    isExact: nestedRoute.isExact,
  };
}
